import {
  Component,
  inject,
  effect,
  signal,
  OnInit,
  OnDestroy,
  ViewChild,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { MatStepper, MatStepperModule } from "@angular/material/stepper";
import { MatButtonModule } from "@angular/material/button";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from "@angular/forms";
import { Store } from "@ngrx/store";
import { ActivatedRoute } from "@angular/router";
import { Actions, ofType } from "@ngrx/effects";
import { toSignal } from "@angular/core/rxjs-interop";
import { MatIconModule } from "@angular/material/icon";
import { MatTooltipModule } from "@angular/material/tooltip";
import {
  STEPPER_GLOBAL_OPTIONS,
  StepperSelectionEvent,
} from "@angular/cdk/stepper";
import { Subject, combineLatest, takeUntil, map, filter, take, debounceTime, distinctUntilChanged } from "rxjs";
import { MipsQuestionnaireComponent } from "@components/mips-questionnaire/mips-questionnaire.component";
import { MipsEligibilityComponent } from "@components/mips-eligibility/mips-eligibility.component";
import { MipsTinEligibilityDetailsComponent } from "@components/mips-tin-eligibility-details/mips-tin-eligibility-details.component";
import { MipsSettingsComponent } from "@components/mips-settings/mips-settings.component";
import { MipsSubmissionConfirmationComponent } from "@components/mips-submission-confirmation/mips-submission-confirmation.component";
import { MipsSubmissionScoreComponent } from "@components/mips-submission-score/mips-submission-score.component";
import { MipsPacketActions } from "@store/actions/mips-packet/mips-packet.actions";
import { MipsQuestionnaireActions } from "@store/actions/mips-questionnaire/mips-questionnaire.actions";
import { MipsEligibilityActions } from "@store/actions/mips-eligibility/mips-eligibility.actions";
import { selectActiveUser } from "@store/selectors/user/user.selectors";
import {
  selectActivePacket,
  selectCurrentStep,
  selectPacketId,
  selectReportingLevel,
  selectReportingType,
  selectIsLoading,
  selectIsSaving,
  selectQualityMeasuresLoading,
  selectSubmissionScoreLoading,
  selectDrcfEnvelopesLoading,
  selectSubmitSuccess,
  selectEffectiveQuestionnaire,
} from "@store/selectors/mips-packet/mips-packet.selector";
import { selectIsLoading as selectIsQuestionnaireLoading } from "@store/selectors/mips-questionnaire/mips-questionnaire.selectors";
import { MipsValidationService } from "@services/mips-validation.service";
import { MipsQualityComponent } from "@components/mips-quality/mips-quality.component";
import { MipsPromotingInteroperabilityComponent } from "@components/mips-pi/mips-pi.component";
import { MipsImprovementActivitiesComponent } from "@components/mips-ia/mips-ia.component";
import { MipsDrcfComponent } from "@components/mips-drcf/mips-drcf.component";
import { getCurrentPerformanceYear, isSubmissionsClosed } from "../../constants/mips-constants";

@Component({
  selector: "app-mips-stepper",
  standalone: true,
  imports: [
    CommonModule,
    MatStepperModule,
    FormsModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    MipsQuestionnaireComponent,
    MipsEligibilityComponent,
    MipsTinEligibilityDetailsComponent,
    MipsSettingsComponent,
    MipsQualityComponent,
    MipsPromotingInteroperabilityComponent,
    MipsImprovementActivitiesComponent,
    MipsDrcfComponent,
    MipsSubmissionConfirmationComponent,
    MipsSubmissionScoreComponent,
  ],
  templateUrl: "./mips-stepper.component.html",
  styleUrl: "./mips-stepper.component.css",
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: STEPPER_GLOBAL_OPTIONS,
      useValue: { displayDefaultIndicatorType: false },
    },
  ],
})
export class MipsStepperComponent implements OnInit, OnDestroy {
  private readonly formBuilder = inject(FormBuilder);
  private readonly store = inject(Store);
  private readonly route = inject(ActivatedRoute);
  private readonly actions$ = inject(Actions);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly validationService = inject(MipsValidationService);
  private readonly destroy$ = new Subject<void>();

  private locallyCompletedSteps = new Set<number>();
  private isDeepLinkStepJumpPending = false;

  isNavigating = false;

  step1FormGroup!: FormGroup;
  step2FormGroup!: FormGroup;
  step3FormGroup!: FormGroup;
  step4FormGroup!: FormGroup;
  step5FormGroup!: FormGroup;
  step6FormGroup!: FormGroup;
  step7FormGroup!: FormGroup;
  step8FormGroup!: FormGroup;
  step9FormGroup!: FormGroup;
  step10FormGroup!: FormGroup;

  @ViewChild("stepper") stepper!: MatStepper;

  activeUser = toSignal(this.store.select(selectActiveUser));
  currentPacket = toSignal(this.store.select(selectActivePacket));
  currentStep = toSignal(this.store.select(selectCurrentStep));
  packetId = toSignal(this.store.select(selectPacketId));
  reportingLevel = toSignal(this.store.select(selectReportingLevel));
  reportingType = toSignal(this.store.select(selectReportingType));
  isSaving = toSignal(this.store.select(selectIsSaving));
  isLoading = toSignal(this.store.select(selectIsLoading));
  // True while any of the packet's fanned-out loads (submission, quality measures,
  // questionnaire, DRCF envelopes, or — for an already-submitted packet — its score) are
  // still in flight, so navigation can be held back until all of it has actually arrived,
  // not just the submission fetch itself.
  private readonly packetDataLoading$ = combineLatest([
    this.store.select(selectIsLoading),
    this.store.select(selectQualityMeasuresLoading),
    this.store.select(selectIsQuestionnaireLoading),
    this.store.select(selectSubmissionScoreLoading),
    this.store.select(selectDrcfEnvelopesLoading),
  ]).pipe(map(([packetLoading, measuresLoading, questionnaireLoading, scoreLoading, drcfLoading]) =>
    packetLoading || measuresLoading || questionnaireLoading || scoreLoading || drcfLoading
  ));
  isPacketDataLoading = toSignal(this.packetDataLoading$, { initialValue: false });
  submitSuccess = toSignal(this.store.select(selectSubmitSuccess));
  questionnaire = toSignal(this.store.select(selectEffectiveQuestionnaire));
  performanceYear = getCurrentPerformanceYear();
  selectedPerformanceYear = signal(getCurrentPerformanceYear());
  isOpenedFromAdminReview = signal(false);

  get isReadOnly(): boolean {
    return isSubmissionsClosed() && this.selectedPerformanceYear() !== getCurrentPerformanceYear();
  }

  constructor() {
    effect(() => {
      const readOnly = isSubmissionsClosed() && this.selectedPerformanceYear() !== getCurrentPerformanceYear();
      // step2FormGroup (eligibility) is always kept enabled so the user can
      // change year/TIN/org and select a different submission at any time.
      const dataSteps = [
        this.step1FormGroup,
        this.step3FormGroup,
        this.step4FormGroup,
        this.step5FormGroup,
        this.step6FormGroup,
        this.step7FormGroup,
        this.step8FormGroup,
        this.step9FormGroup,
      ];
      setTimeout(() => {
        if (readOnly) {
          dataSteps.forEach(fg => fg?.disable({ emitEvent: false }));
        } else {
          dataSteps.forEach(fg => fg?.enable({ emitEvent: false }));
        }
      }, 0);
    });
  }

  ngOnInit(): void {
    this.initializeForms();
    this.loadInitialData();
    this.setupNavigationHandler();
    this.syncLocalCompletionWithPacket();
    this.loadDeepLinkedSubmission();
  }

  // Reads reactively (not route.snapshot) because Angular's default
  // RouteReuseStrategy can reuse this component instance across two
  // navigations to this same route that only differ by query param —
  // a snapshot read in ngOnInit would silently miss the second one.
  private loadDeepLinkedSubmission(): void {
    this.route.queryParamMap
      .pipe(
        map((params) => ({
          openSubmissionId: params.get("openSubmissionId"),
          fromAdminReview: params.get("fromAdminReview") === "true",
        })),
        distinctUntilChanged((previous, current) => previous.openSubmissionId === current.openSubmissionId),
        filter((value): value is { openSubmissionId: string; fromAdminReview: boolean } => !!value.openSubmissionId),
        takeUntil(this.destroy$),
      )
      .subscribe(({ openSubmissionId, fromAdminReview }) => {
        this.isDeepLinkStepJumpPending = true;
        this.isOpenedFromAdminReview.set(fromAdminReview);
        this.store.dispatch(
          MipsPacketActions.loadExistingPacket({ packetId: Number(openSubmissionId) }),
        );
      });
  }

  ngOnDestroy(): void {
    this.store.dispatch(MipsPacketActions.resetPacket());
    this.store.dispatch(MipsEligibilityActions.clearEligibilityResults());

    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeForms(): void {
    this.step1FormGroup = this.formBuilder.group({});
    this.step2FormGroup = this.formBuilder.group({});
    this.step3FormGroup = this.formBuilder.group({});
    this.step4FormGroup = this.formBuilder.group({});
    this.step5FormGroup = this.formBuilder.group({});
    this.step6FormGroup = this.formBuilder.group({});
    this.step7FormGroup = this.formBuilder.group({});
    this.step8FormGroup = this.formBuilder.group({});
    this.step9FormGroup = this.formBuilder.group({});
    this.step10FormGroup = this.formBuilder.group({});
  }

  private loadInitialData(): void {
    const user = this.activeUser();

    if (user?.orgId) {
      this.store.dispatch(
        MipsQuestionnaireActions.loadMIPSQuestionnaire({
          orgId: user.orgId.toString(),
          performanceYear: this.performanceYear,
        })
      );
    }
  }

  private setupNavigationHandler(): void {
    this.actions$
      .pipe(
        ofType(
          MipsPacketActions.getOrCreatePacketSuccess,
          MipsPacketActions.saveTINDetailsSuccess,
          MipsPacketActions.saveSettingsSuccess,
          MipsPacketActions.savePIAttestationsSuccess,
          MipsPacketActions.savePIMeasurementsSuccess,
          MipsPacketActions.saveIAMeasurementsSuccess,
          MipsPacketActions.updateDRCFStatusSuccess
        ),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.cdr.markForCheck();
      });

    this.actions$
      .pipe(ofType(MipsPacketActions.navigateToStep), takeUntil(this.destroy$))
      .subscribe(({ stepIndex }) => {
        this.navigateToStepIndex(stepIndex);
      });

    this.actions$
      .pipe(
        ofType(
          MipsPacketActions.submitPacketSuccess,
          MipsPacketActions.submitMultiplePacketsSuccess,
        ),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        this.navigateToStepIndex(9);
      });

    this.actions$
      .pipe(ofType(MipsPacketActions.loadExistingPacketSuccess), takeUntil(this.destroy$))
      .subscribe(() => {
        if (this.isDeepLinkStepJumpPending) {
          this.isDeepLinkStepJumpPending = false;
          this.jumpToConfirmationStepForDeepLink();
        }
      });
  }

  private syncLocalCompletionWithPacket(): void {
    this.store
      .select(selectActivePacket)
      .pipe(takeUntil(this.destroy$))
      .subscribe((packet) => {
        if (!packet || !packet.id) {
          this.locallyCompletedSteps.clear();
          this.cdr.markForCheck();
          return;
        }

        this.locallyCompletedSteps.clear();

        if (packet.id) {
          this.locallyCompletedSteps.add(1);
        }

        if (
          packet.id &&
          packet.npi &&
          packet.reportingType &&
          packet.reportingLevel
        ) {
          this.locallyCompletedSteps.add(2);
        }

        if (packet.tinDetails?.tinStatus === "approved") {
          this.locallyCompletedSteps.add(3);
        }

        if (packet.settings?.cehrtId) {
          this.locallyCompletedSteps.add(4);
        }

        const minMeasures = 3;
        if ((packet.quality?.selectedMeasureIds?.length ?? 0) >= minMeasures) {
          this.locallyCompletedSteps.add(5);
        }

        if (packet.promotingInteroperability?.attestations) {
          this.locallyCompletedSteps.add(6);
        }

        if (
          packet.improvementActivities &&
          packet.improvementActivities.activities &&
          packet.improvementActivities.activities.length > 0
        ) {
          this.locallyCompletedSteps.add(7);
        }

        this.locallyCompletedSteps.add(8);

        const excluded = this.validationService.getExcludedCategories(this.questionnaire());
        if (excluded.has("quality")) this.locallyCompletedSteps.add(5);
        if (excluded.has("pi")) this.locallyCompletedSteps.add(6);
        if (excluded.has("ia")) this.locallyCompletedSteps.add(7);

        this.cdr.markForCheck();
      });
  }

  isStepExcluded(stepNumber: number): boolean {
    const excluded = this.validationService.getExcludedCategories(this.questionnaire());
    return (
      (stepNumber === 5 && excluded.has("quality")) ||
      (stepNumber === 6 && excluded.has("pi")) ||
      (stepNumber === 7 && excluded.has("ia"))
    );
  }

  onNext(currentStepNumber: number): void {
    if (this.isNavigating || this.isPacketDataLoading()) {
      return;
    }

    if (!this.isReadOnly) {
      const formGroup = this.getFormGroup(currentStepNumber);
      if (!this.isStepExcluded(currentStepNumber) && !formGroup.valid) {
        formGroup.markAllAsTouched();
        return;
      }
    }

    // In read-only mode, still require a submission to be loaded before advancing past eligibility
    if (this.isReadOnly && currentStepNumber >= 2 && !this.currentPacket()?.id) {
      return;
    }

    this.locallyCompletedSteps.add(currentStepNumber);

    this.isNavigating = true;

    setTimeout(() => {
      this.stepper.next();
      const newStepNumber = this.stepper.selectedIndex + 1;
      this.store.dispatch(
        MipsPacketActions.setCurrentStep({ stepNumber: newStepNumber })
      );
      this.isNavigating = false;
      this.cdr.markForCheck();
    }, 100);
  }

  onBack(): void {
    if (this.isNavigating) {
      return;
    }

    this.isNavigating = true;

    setTimeout(() => {
      this.stepper.previous();
      const newStepNumber = this.stepper.selectedIndex + 1;
      this.store.dispatch(
        MipsPacketActions.setCurrentStep({ stepNumber: newStepNumber })
      );
      this.isNavigating = false;
      this.cdr.markForCheck();
    }, 100);
  }

  onStepChange(event: StepperSelectionEvent): void {
    if (this.isNavigating) {
      event.selectedStep.interacted = false;
      this.stepper.selectedIndex = event.previouslySelectedIndex;
      return;
    }

    const previousIndex = event.previouslySelectedIndex;
    const targetIndex = event.selectedIndex;

    // Hold the user back from moving forward into a step whose data (quality measures,
    // questionnaire, etc.) hasn't finished loading yet — going back is still allowed.
    if (targetIndex > previousIndex && this.isPacketDataLoading()) {
      event.selectedStep.interacted = false;
      this.stepper.selectedIndex = previousIndex;
      this.cdr.markForCheck();
      return;
    }

    if (targetIndex < previousIndex) {
      const targetStepNumber = targetIndex + 1;
      this.store.dispatch(
        MipsPacketActions.setCurrentStep({ stepNumber: targetStepNumber })
      );
      this.cdr.markForCheck();
      return;
    }

    // Block navigation to score page (step 10, index 9) unless submitted
    if (targetIndex === 9 && !this.submitSuccess()) {
      event.selectedStep.interacted = false;
      this.stepper.selectedIndex = previousIndex;
      this.cdr.markForCheck();
      return;
    }

    for (let step = 1; step <= targetIndex; step++) {
      if (!this.isStepComplete(step)) {
        event.selectedStep.interacted = false;
        this.stepper.selectedIndex = previousIndex;
        this.cdr.markForCheck();
        return;
      }
    }

    const currentStepNumber = previousIndex + 1;
    const currentForm = this.getFormGroup(currentStepNumber);
    if (!currentForm.valid) {
      currentForm.markAllAsTouched();
      event.selectedStep.interacted = false;
      this.stepper.selectedIndex = previousIndex;
      return;
    }

    this.locallyCompletedSteps.add(currentStepNumber);

    const targetStepNumber = targetIndex + 1;
    this.store.dispatch(
      MipsPacketActions.setCurrentStep({ stepNumber: targetStepNumber })
    );
    this.cdr.markForCheck();
  }

  private navigateToStepIndex(stepIndex: number): void {
    if (!this.stepper || this.isNavigating) {
      return;
    }

    setTimeout(() => {
      this.stepper.selectedIndex = stepIndex;
      this.store.dispatch(
        MipsPacketActions.setCurrentStep({ stepNumber: this.stepper.selectedIndex + 1 })
      );
      this.cdr.markForCheck();
    }, 100);
  }

  // A deep-linked submission skips the normal step-by-step navigation that would
  // otherwise populate locallyCompletedSteps, and its related data (quality
  // measures, questionnaire, DRCF envelopes) may still be loading — onStepChange's
  // guards would otherwise bounce the jump back to step 1. Wait for loading to
  // settle and mark the prior steps complete before jumping straight to
  // Review & Submit (index 8).
  private jumpToConfirmationStepForDeepLink(): void {
    this.packetDataLoading$
      .pipe(
        // A sibling component (e.g. the DRCF step) may kick off its own load in
        // reaction to the same packet update on a separate subscription, with no
        // guaranteed ordering against this one — debounce so a loading flag that's
        // about to flip true isn't missed by an immediate "nothing is loading" read.
        debounceTime(150),
        filter((loading) => !loading),
        take(1),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        for (let step = 1; step <= 8; step++) {
          this.locallyCompletedSteps.add(step);
        }
        this.navigateToStepIndex(8);
      });
  }

  isStepComplete(stepNumber: number): boolean {
    if (this.locallyCompletedSteps.has(stepNumber)) {
      return true;
    }

    const excluded = this.validationService.getExcludedCategories(this.questionnaire());
    if (
      (stepNumber === 5 && excluded.has("quality")) ||
      (stepNumber === 6 && excluded.has("pi")) ||
      (stepNumber === 7 && excluded.has("ia"))
    ) {
      return true;
    }

    const packet = this.currentPacket();
    if (!packet) return false;

    switch (stepNumber) {
      case 1:
        return !!packet.id;

      case 2:
        return !!(
          packet.id &&
          packet.npi &&
          packet.reportingType &&
          packet.reportingLevel
        );

      case 3:
        return packet.tinDetails?.tinStatus === "approved";

      case 4:
        return !!packet.settings?.cehrtId;

      case 5:
        const minMeasures = 3;
        return (packet.quality?.selectedMeasureIds?.length ?? 0) >= minMeasures;

      case 6:
        return !!packet.promotingInteroperability?.attestations;

      case 7:
        return !!(
          packet.improvementActivities &&
          packet.improvementActivities.activities &&
          packet.improvementActivities.activities.length > 0
        );

      case 8:
        return true;

      case 9:
        return this.submitSuccess() ?? false;

      default:
        return false;
    }
  }

  private getFormGroup(stepNumber: number): FormGroup {
    switch (stepNumber) {
      case 1:
        return this.step1FormGroup;
      case 2:
        return this.step2FormGroup;
      case 3:
        return this.step3FormGroup;
      case 4:
        return this.step4FormGroup;
      case 5:
        return this.step5FormGroup;
      case 6:
        return this.step6FormGroup;
      case 7:
        return this.step7FormGroup;
      case 8:
        return this.step8FormGroup;
      case 9:
        return this.step9FormGroup;
      case 10:
        return this.step10FormGroup;
      default:
        return this.step1FormGroup;
    }
  }

  onQuestionnaireYearChange(year: number): void {
    this.selectedPerformanceYear.set(year);
    this.store.dispatch(MipsPacketActions.clearPacket());
    this.store.dispatch(MipsEligibilityActions.clearEligibilityResults());
  }

  onEligibilityYearChange(year: number): void {
    this.selectedPerformanceYear.set(year);
  }
}